// Bolha de mensagem. Cobre todas as combinações:
//   - direction=INBOUND (cliente)   → bolha esquerda neutra
//   - direction=OUTBOUND source=AI  → bolha direita roxa, label "IA"
//   - direction=OUTBOUND source=HUMAN→ bolha direita verde-clara, label do autor
//     ("Você" nas minhas, o nome nas dos outros, "Loja" quando saiu sem atendente)
//   - source=SYSTEM                  → divisor centralizado
// types: TEXT/QUOTE/IMAGE/AUDIO/FILE/HANDOFF/TEMPLATE/STICKER/CONTACTS
// Extras: citação no topo (replyTo), reações embaixo e, só na thread ao vivo (`actions`),
// a barra de reagir/responder que aparece no hover ou no foco.
//
// SRP: só apresentação. Métricas/handler vêm do parent.
import { memo, useEffect, useRef, useState } from 'react';
import { useAppSelector } from '@/app/store/hooks';
import { selectCurrentUser } from '@/features/auth';
import { CheckIcon, FileTextIcon } from '@/components/icons';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/utils/cn';
import { formatBRL } from '@/lib/utils/formatters';
import type { InboxMessage } from '../api';
import { ContactsBubble } from './ContactsBubble';
import { BubbleToolbar, ReactionChips } from './MessageReactions';
import { PhoneLink } from './PhoneActions';
import { QuotedBlock } from './QuotedBlock';
import { formatBubbleTime, formatTimelineStamp } from '../utils/time';
import { formatRelativeDate } from '@/lib/utils/date';
import { formatWhatsAppText, type PhoneRenderer } from '../utils/formatWhatsAppText';
import styles from './MessageBubble.module.css';

/**
 * Quem enviou, na ordem que não mente em nenhum caso.
 *
 * 🔴 Antes era `message.authorName ?? 'Você'`, e `authorName` **não existe no servidor**
 * (o nome vem em `author`, aninhado). Resultado: toda mensagem enviada dizia "Você",
 * inclusive ao abrir a conversa de OUTRO atendente, na mesma tela que avisa "você está
 * acompanhando o atendimento de outro atendente".
 *
 * `authorId` é conferido ANTES do objeto: a bolha otimista e qualquer payload sem o join
 * ainda são minhas. Sem autor nenhum é saída sem atendente no sistema (celular da loja e
 * histórico importado, que gravam OUTBOUND/HUMAN com autor nulo de propósito) — aí o
 * rótulo diz a origem em vez de inventar uma pessoa.
 */
function rotuloDoAutor(message: InboxMessage, meuId: string | null): string | null {
  if (message.direction !== 'OUTBOUND') return null;
  if (message.source === 'AI') return 'IA';
  if (message.authorId && message.authorId === meuId) return 'Você';
  if (message.author?.name) return message.author.name;
  if (message.authorId) return 'Atendente';
  return 'Loja';
}

/**
 * Ações da bolha que só existem na thread ao vivo (reagir, responder, ir até a citada).
 * Callbacks recebem a mensagem para o objeto poder ser estável no pai e o `memo` valer.
 */
export interface MessageBubbleActions {
  onOpenQuoted: (messageId: string) => void;
  /** Sem reagir/responder (ex.: janela de 24h fechada) a barra não aparece. */
  onReact?: (message: InboxMessage, emoji: string) => void;
  onReply?: (message: InboxMessage) => void;
}

interface Props {
  message: InboxMessage;
  /** Ausente nas telas só de leitura (atendimento anterior, sessão, prévia da Fila). */
  actions?: MessageBubbleActions;
}

/** Telefone solto no texto vira botão com "Copiar número" e "Iniciar conversa". */
const renderPhone: PhoneRenderer = (m, key) => <PhoneLink key={key} raw={m.raw} digits={m.digits} />;

/** A bolha já existe no servidor e saiu da fila de envio: dá para reagir e responder. */
function acionavel(message: InboxMessage): boolean {
  if (message.id.startsWith('optimistic-')) return false;
  return message.status !== 'PENDING' && message.status !== 'FAILED';
}

function MessageBubbleBase({ message, actions }: Props) {
  // A bolha lê o usuário daqui, e não por prop: ela é montada em quatro telas (thread,
  // atendimento anterior, modal de sessão e prévia da Fila) e três delas não têm o
  // usuário em mãos. O seletor é estável, então o `memo` continua valendo.
  const meuId = useAppSelector(selectCurrentUser)?.id ?? null;

  // SYSTEM = divisor centralizado (ex: handoff IA→humano).
  if (message.source === 'SYSTEM') {
    return (
      <div className={styles.systemRow} role="separator">
        <span className={styles.systemPill}>
          <CheckIcon size={12} />
          {message.text ?? 'Evento do sistema'}
        </span>
      </div>
    );
  }

  const isOutbound = message.direction === 'OUTBOUND';
  const isAi = isOutbound && message.source === 'AI';
  const sideClass = isOutbound ? styles.right : styles.left;
  const bubbleClass = isAi ? styles.aiBubble : isOutbound ? styles.myBubble : styles.themBubble;
  const labelText = rotuloDoAutor(message, meuId);

  // O instante COMPLETO, com data e idade. A bolha mostra só `HH:mm` (o dia vem do
  // cabeçalho de bloco), mas quem precisa do exato — "isso foi mesmo dia 17?" — tem
  // de conseguir a resposta sem sair da conversa. Vale para o leitor de tela também:
  // ali não existe "passar o mouse", e o `aria-label` era a única fonte de tempo.
  const carimbo = `${formatTimelineStamp(message.createdAt)} · ${formatRelativeDate(
    message.createdAt,
  )}`;

  const ariaLabel = `Mensagem ${isOutbound ? 'enviada' : 'recebida'} ${
    isAi ? 'pela IA' : labelText ? `por ${labelText}` : ''
  } em ${carimbo}`;

  const reactions = message.reactions ?? [];
  const storeEmoji = reactions.find((r) => r.actor === 'STORE')?.emoji ?? '';
  const onReact = actions?.onReact;
  const onReply = actions?.onReply;
  const showToolbar = !!onReact && !!onReply && acionavel(message);
  const quoted = message.replyTo ? (
    <QuotedBlock replyTo={message.replyTo} onOpen={actions?.onOpenQuoted} />
  ) : null;
  const meta = <BubbleMeta message={message} isOutbound={isOutbound} carimbo={carimbo} />;
  const isSticker = message.type === 'STICKER' && !!message.mediaUrl;
  const contacts = message.type === 'CONTACTS' ? message.metadata?.contacts : undefined;

  return (
    <article
      className={cn(styles.row, sideClass)}
      data-direction={message.direction}
      data-source={message.source}
      data-message-id={message.id}
      aria-label={ariaLabel}
    >
      <div className={styles.col}>
        {labelText && <span className={cn(styles.label, isAi && styles.labelAi)}>{labelText}</span>}
        {showToolbar && (
          <div className={styles.toolbarSlot}>
            <BubbleToolbar
              storeEmoji={storeEmoji}
              onReact={(emoji) => onReact?.(message, emoji)}
              onReply={() => onReply?.(message)}
            />
          </div>
        )}
        {isSticker ? (
          // Figurinha sem balão, como no WhatsApp. O <img> toca webp animado sozinho.
          <div className={styles.stickerWrap}>
            {quoted}
            <img
              className={styles.sticker}
              src={message.mediaUrl!}
              alt={message.metadata?.animated ? 'Figurinha animada' : 'Figurinha'}
              loading="lazy"
            />
            {meta}
          </div>
        ) : (
          <div className={cn(styles.bubble, bubbleClass)}>
            {quoted}
            {message.type === 'QUOTE' && message.quote ? (
              <QuoteContent
                text={message.text}
                items={message.quote.items}
                shippingCents={message.quote.shippingCents}
                totalCents={message.quote.totalCents}
              />
            ) : message.type === 'IMAGE' && message.mediaUrl ? (
              <ImageBubble src={message.mediaUrl} caption={message.text} />
            ) : message.type === 'AUDIO' && message.mediaUrl ? (
              <div className={styles.audioWrap}>
                <audio className={styles.audio} controls preload="metadata" src={message.mediaUrl}>
                  <track kind="captions" />
                </audio>
                {message.text && <span className={styles.text}>{message.text}</span>}
              </div>
            ) : message.type === 'VIDEO' && message.mediaUrl ? (
              <div className={styles.videoWrap}>
                {/* Sem autoplay: a thread pode ter vários vídeos e o proxy de mídia
                    serve por Range — `preload="metadata"` puxa só o cabeçalho. */}
                <video className={styles.video} controls preload="metadata" src={message.mediaUrl}>
                  <track kind="captions" />
                </video>
                {message.text && <span className={styles.text}>{message.text}</span>}
              </div>
            ) : message.type === 'FILE' && message.mediaUrl ? (
              <div className={styles.fileWrap}>
                <a
                  className={styles.file}
                  href={message.mediaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                >
                  <FileTextIcon size={20} />
                  <span className={styles.fileName}>{message.metadata?.filename ?? 'Arquivo'}</span>
                </a>
                {message.text && <span className={styles.text}>{message.text}</span>}
              </div>
            ) : contacts && contacts.length > 0 ? (
              <ContactsBubble contacts={contacts} />
            ) : message.type === 'TEMPLATE' ? (
              <span className={styles.text}>
                📋 Template: <strong>{message.templateName ?? '—'}</strong>
                {message.text ? `, ${message.text}` : ''}
              </span>
            ) : (
              <span className={styles.text}>
                {formatWhatsAppText(message.text ?? '', renderPhone)}
              </span>
            )}
            {meta}
          </div>
        )}
        <ReactionChips reactions={reactions} />
      </div>
    </article>
  );
}

interface BubbleMetaProps {
  message: InboxMessage;
  isOutbound: boolean;
  carimbo: string;
}

/** Hora + status de entrega, no canto da bolha (ou embaixo da figurinha). */
function BubbleMeta({ message, isOutbound, carimbo }: BubbleMetaProps) {
  return (
    <span className={styles.meta}>
      <span className={styles.time} title={carimbo}>
        {formatBubbleTime(message.createdAt)}
      </span>
      {isOutbound && message.status === 'READ' && (
        <span className={cn(styles.checks, styles.checksRead)} aria-label="Lida">
          <CheckIcon size={12} />
          <CheckIcon size={12} />
        </span>
      )}
      {isOutbound && message.status === 'DELIVERED' && (
        <span className={styles.checks} aria-label="Entregue">
          <CheckIcon size={12} />
          <CheckIcon size={12} />
        </span>
      )}
      {isOutbound && message.status === 'SENT' && (
        <span className={styles.checks} aria-label="Enviada">
          <CheckIcon size={12} />
        </span>
      )}
      {isOutbound && message.status === 'PENDING' && (
        <span className={styles.pending} aria-label="Enviando">
          Enviando…
        </span>
      )}
      {isOutbound && message.status === 'FAILED' && (
        <span className={styles.failed} aria-label="Falhou">
          Falhou
        </span>
      )}
    </span>
  );
}

/**
 * Imagem estilo WhatsApp: thumbnail limitado na bolha (não estoura o layout) +
 * lightbox ao clicar (overlay limpo via <dialog> nativo — Escape e clique no
 * backdrop fecham, com foco preso automático).
 */
function ImageBubble({ src, caption }: { src: string; caption: string | null }) {
  const [open, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <figure className={styles.image}>
      <button
        type="button"
        className={styles.imageThumb}
        onClick={() => setOpen(true)}
        aria-label="Ampliar imagem"
      >
        <img src={src} alt={caption ?? 'Imagem recebida'} loading="lazy" />
      </button>
      {caption && <figcaption>{caption}</figcaption>}
      {/* Backdrop-click fecha; o teclado é coberto nativamente pelo <dialog>
          (Escape → evento close). Daí o disable das regras a11y de click. */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
      <dialog
        ref={dialogRef}
        className={styles.lightbox}
        onClose={() => setOpen(false)}
        onClick={(e) => {
          if (e.target === dialogRef.current) setOpen(false);
        }}
      >
        <img src={src} alt={caption ?? 'Imagem recebida'} className={styles.lightboxImg} />
      </dialog>
    </figure>
  );
}

interface QuoteContentProps {
  text: string | null;
  items: { description: string; quantity: number; unitCents: number; totalCents: number }[];
  shippingCents: number;
  totalCents: number;
}

function QuoteContent({ text, items, shippingCents, totalCents }: QuoteContentProps) {
  return (
    <div className={styles.quote}>
      <Badge variant="purple">ORÇAMENTO</Badge>
      {text && <p className={styles.quoteIntro}>{text}</p>}
      <ul className={styles.quoteItems}>
        {items.map((it, i) => (
          <li key={`${it.description}-${i}`} className={styles.quoteItem}>
            <span>
              {it.quantity}× {it.description}
            </span>
            <strong>{formatBRL(it.totalCents)}</strong>
          </li>
        ))}
      </ul>
      {shippingCents > 0 && (
        <div className={styles.quoteRow}>
          <span>Frete</span>
          <span>{formatBRL(shippingCents)}</span>
        </div>
      )}
      <div className={cn(styles.quoteRow, styles.quoteTotalRow)}>
        <strong>Total</strong>
        <strong className={styles.quoteTotal}>{formatBRL(totalCents)}</strong>
      </div>
    </div>
  );
}

export const MessageBubble = memo(MessageBubbleBase);
